import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { userProfileApi } from 'services/apiDigitalTicket/userProfile';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Types
import { ApplicationState } from 'store';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import USER from 'constants/userProfile';

import * as UserProfileActions from './actions';
import * as UsersActions from '../users/actions';
import { UserProfileTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof UserProfileActions.listUserProfileRequest>): any {
  try {
    const transportOperator = yield select(
      (state: ApplicationState) =>
        state.transportOperators.transportOperatorSelected,
    );

    yield put(DialogsActions.setOpenLoading({ title: 'Carregando perfis' }));
    const response = yield call(userProfileApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UserProfileActions.listUserProfileSuccess({
          userProfileList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - listProfile - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.listUserProfileFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listRequest({
  payload,
}: ReturnType<typeof UserProfileActions.listUserProfileRequestsRequest>): any {
  try {
    const transportOperator = yield select(
      (state: ApplicationState) =>
        state.transportOperators.transportOperatorSelected,
    );

    yield put(DialogsActions.setOpenLoading({ title: 'Carregando perfis' }));

    const response = yield call(userProfileApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UserProfileActions.listUserProfileRequestsSuccess({
          userProfileListRequest: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - listProfile Request - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.listUserProfileRequestsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listAvailable({
  payload,
}: ReturnType<typeof UserProfileActions.listUserProfileAvailableRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando perfis' }));
    const response = yield call(userProfileApi.listAvailable, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        UserProfileActions.listUserProfileAvailableSuccess({
          userProfileListAvailable: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - listProfile Available - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.listUserProfileAvailableFailure());
    yield put(DialogsActions.setClose());
  }
}

function* request({
  payload,
}: ReturnType<typeof UserProfileActions.requestUserProfileRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Criando pedido de perfil' }),
    );
    const response = yield call(userProfileApi.request, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        UserProfileActions.requestUserProfileSuccess({
          userProfileData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido de perfil criado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - request - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.requestUserProfileFailure());
    yield put(DialogsActions.setClose());
  } finally {
    yield put(
      UsersActions.listAppUsersRequest({
        body: { _includes: 'transportcard' },
      }),
    );
  }
}

function* updateData({
  payload,
}: ReturnType<typeof UserProfileActions.updateUserProfileDataRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Editando pedido de perfil' }),
    );
    const response = yield call(userProfileApi.updateData, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        UserProfileActions.updateUserProfileDataSuccess({
          userProfileData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido de perfil editado com sucesso',
        }),
      );
      window.location.reload();
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - request - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.updateUserProfileDataFailure());
    yield put(DialogsActions.setClose());
  } finally {
    yield put(
      UsersActions.listAppUsersRequest({
        body: { _includes: 'transportcard' },
      }),
    );
  }
}

function* attachment({
  payload,
}: ReturnType<
  typeof UserProfileActions.requestUserProfileAttachmentRequest
>): any {
  try {
    const response = yield call(userProfileApi.attachment, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        UserProfileActions.requestUserProfileAttachmentSuccess({
          userProfileAttachmentList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Imagem anexada com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - requestAttachment - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.requestUserProfileAttachmentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof UserProfileActions.updateUserProfileRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atualizando pedido de perfil' }),
    );
    const response = yield call(userProfileApi.update, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        UserProfileActions.updateUserProfileSuccess({
          userProfileData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido de perfil atualizado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - createUserProfile - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.updateUserProfileFailure());
    yield put(DialogsActions.setClose());
  } finally {
    yield put(
      UsersActions.listAppUsersRequest({
        body: { _includes: 'transportcard' },
      }),
    );
  }
}

function* updateStatus({
  payload,
}: ReturnType<typeof UserProfileActions.updateUserProfileStatusRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Atualizando status' }));
    const response = yield call(userProfileApi.updateStatus, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        UserProfileActions.updateUserProfileStatusSuccess({
          userProfileData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Status atualizado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - createUserProfile - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.updateUserProfileStatusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateRequest({
  payload,
}: ReturnType<
  typeof UserProfileActions.updateUserProfileRequestsRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Atualizando...' }));
    const startDate = yield select(
      (state: ApplicationState) => state.reports.selectedStartDate,
    );
    const endDate = yield select(
      (state: ApplicationState) => state.reports.selectedEndDate,
    );

    const response = yield call(userProfileApi.updateRequest, {
      data: { ...payload, selectedValidationStatus: undefined },
    });

    const storegeLimit =
      JSON.parse(window.localStorage.getItem('persist:root') as string)
        ?.localLimit || '10';
    const { localLimit } = JSON.parse(storegeLimit) || 10;

    if (response.status === 200) {
      yield put(
        UserProfileActions.updateUserProfileRequestsSuccess({
          userProfileData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        UserProfileActions.listUserProfileRequestsRequest({
          startDate: new Date(startDate).getTime(),
          endDate: new Date(endDate).getTime(),
          status: `[${(payload as {
            selectedValidationStatus: string[];
          }).selectedValidationStatus.join()}]`,
          _includes: 'userprofile',
          _limit: localLimit,
        }),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Atualizado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - createUserProfile - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.updateUserProfileRequestsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateExpiration({
  payload,
}: ReturnType<
  typeof UserProfileActions.updateUserProfileExpirationRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Atualizando...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(userProfileApi.updateExpiration, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(UserProfileActions.updateUserProfileExpirationSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Atualizado com sucesso',
        }),
      );
      window.location.reload(true);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - UserProfile Expiration - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.updateUserProfileExpirationFailure());
    yield put(DialogsActions.setClose());
  }
}

function* userProfileImport({
  payload,
  meta,
}: ReturnType<typeof UserProfileActions.userProfileImportRequest>): any {
  try {
    const formData = new FormData();
    formData.append('attachment', payload.attachment[0]);
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    yield put(DialogsActions.setOpenLoading({ title: 'Aguarde..' }));

    const response = yield call(userProfileApi.userProfileImport, {
      formData,
      importType: payload.importType,
      fileName: payload.fileName,
      transportOperatorId: transportOperator.id,
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Arquivo xml importado com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(UserProfileActions.userProfileImportSuccess());
      // yield window.location.reload(); // force list
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    meta.promise.resolve(error.response);

    console.error('userProfile - UserProfile Import - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.userProfileImportFailure());
    yield put(DialogsActions.setClose());
  }
}

function* userProfileListImports({
  payload,
}: ReturnType<typeof UserProfileActions.userProfileListImportsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(userProfileApi.userProfileListImports, {
      params: { ...payload, transportOperatorId: transportOperator.id },
    });

    if (response.status === 200) {
      yield put(
        UserProfileActions.userProfileListImportsSuccess({
          userProfileListImportsData: response.data.map((e: any) => ({
            ...e,
            status:
              USER.XML_STATUS.find((status) => status.KEY === e.status)
                ?.VALUE || '',
          })),
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - List Imports - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.userProfileListImportsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* manualRelease({
  payload,
}: ReturnType<typeof UserProfileActions.userProfileManualReleaseRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Processando baixa manual...' }));
    const response = yield call(userProfileApi.manualRelease, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        UserProfileActions.userProfileManualReleaseSuccess({
          userProfileData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Baixa manual realizada com sucesso',
        }),
      );
      yield put(
        UsersActions.getAppUserRequest({
          id: response.data.userId,
          _includes: 'profile,userprofile',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('userProfile - manualRelease - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UserProfileActions.userProfileManualReleaseFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const userProfileSagas = [
  takeLatest(UserProfileTypes.LIST_USERPROFILE_REQUEST, list),
  takeLatest(
    UserProfileTypes.REQUEST_USERPROFILE_ATTACHMENT_REQUEST,
    attachment,
  ),
  takeLatest(UserProfileTypes.LIST_USERPROFILE_REQUESTS_REQUEST, listRequest),
  takeLatest(
    UserProfileTypes.LIST_USERPROFILE_AVAILABLE_REQUEST,
    listAvailable,
  ),
  takeLatest(UserProfileTypes.REQUEST_USERPROFILE_REQUEST, request),
  takeLatest(UserProfileTypes.UPDATE_USERPROFILE_REQUEST, update),
  takeLatest(UserProfileTypes.UPDATE_USERPROFILE_STATUS_REQUEST, updateStatus),
  takeLatest(
    UserProfileTypes.UPDATE_USERPROFILE_REQUESTS_REQUEST,
    updateRequest,
  ),
  takeLatest(
    UserProfileTypes.REQUEST_USERPROFILE_EXPIRATION_REQUEST,
    updateExpiration,
  ),
  takeLatest(UserProfileTypes.UPDATE_USERPROFILE_DATA_REQUEST, updateData),
  takeLatest(UserProfileTypes.USER_PROFILE_IMPORT_REQUEST, userProfileImport),
  takeLatest(
    UserProfileTypes.USER_PROFILE_LIST_IMPORTS_REQUEST,
    userProfileListImports,
  ),
  takeLatest(
    UserProfileTypes.USER_PROFILE_MANUAL_RELEASE_REQUEST,
    manualRelease,
  ),
];
